// src/components/ContentColumn.js

import { FiInfo, FiServer, FiTv, FiChevronDown } from 'react-icons/fi';
import { getGenreName } from '../helpers/genreHelper';

const SERVERS = ["player.videasy.net", "vidsrc.cc", "vidsrc.me"];

export default function ContentColumn({
  item,
  currentServer,
  setCurrentServer,
  seasons,
  episodes,
  selectedSeason,
  setSelectedSeason,
  selectedEpisode,
  setSelectedEpisode,
  isLoading,
  isEpisodeReleased
}) {

  const currentEpisodeData = episodes.find(e => e.episode_number === selectedEpisode);

  return (
    <>
      {/* Title - CENTERED */}
      <div>
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2 text-center">{item.title || item.name}</h2>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-900 text-green-300 text-xs font-medium">{Math.round(item.vote_average * 10)}% Match</span>
          <span className="text-gray-300 text-xs sm:text-sm">{item.release_date?.substring(0, 4) || item.first_air_date?.substring(0, 4)}</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-900 text-blue-100">{item.media_type === 'movie' ? 'Movie' : 'TV Show'}</span>
        </div>
      </div>

      {/* Server Selection */}
      <div className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-700">
        <div className="flex items-center gap-2 mb-2 sm:mb-3"><FiServer className="text-blue-400 text-sm sm:text-base" /><h3 className="text-base sm:text-lg font-semibold text-white">Streaming Server</h3></div>
        <div className="flex flex-wrap gap-2">
          {SERVERS.map((server, index) => (<button key={server} onClick={() => setCurrentServer(server)} className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${currentServer === server ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>Server {index + 1}</button>))}
        </div>
      </div>

      {/* Episode Selection */}
      {item.media_type === 'tv' && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-4 sm:p-6 border border-gray-600 shadow-lg">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600 bg-opacity-20 rounded-lg"><FiTv className="text-purple-400 text-lg sm:text-xl" /></div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white">Episode Selection</h3>
                {episodes.length > 0 && <p className="text-xs sm:text-sm text-gray-400">{episodes.filter(ep => isEpisodeReleased(ep.air_date)).length} of {episodes.length} episodes available</p>}
              </div>
            </div>
          </div>
          <div className="space-y-4 sm:space-y-6">
            {/* Season Selector */}
            <div className="relative group">
              <select value={selectedSeason} onChange={(e) => setSelectedSeason(Number(e.target.value))} disabled={isLoading} className="w-full bg-gray-700 text-white p-2.5 sm:p-3 rounded-xl appearance-none border-2 border-gray-600 hover:border-purple-500 focus:border-purple-400 focus:ring-2 focus:ring-purple-400 focus:ring-opacity-50 text-sm sm:text-base font-medium transition-all cursor-pointer">
                {seasons.map(season => {
                  // Create display text for season
                  let seasonText = `Season ${season.season_number}`;
                  
                  // Add season name if available and not just generic text
                  if (season.name && season.name.trim() && !season.name.toLowerCase().includes(`season ${season.season_number}`)) {
                    seasonText += `: ${season.name}`;
                  }
                  
                  // Add episode count if available
                  if (season.episode_count) {
                    seasonText += ` (${season.episode_count} episodes)`;
                  }
                  
                  return (
                    <option key={season.id} value={season.season_number} className="bg-gray-700 text-white py-2">
                      {seasonText}
                    </option>
                  );
                })}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"><FiChevronDown className="text-gray-400 group-hover:text-purple-400 transition-colors duration-200 text-lg" /></div>
            </div>
            {/* Episode Selector */}
            <div className="relative group">
              <select value={selectedEpisode} onChange={(e) => setSelectedEpisode(Number(e.target.value))} disabled={isLoading || !episodes.length} className="w-full bg-gray-700 text-white p-2.5 sm:p-3 rounded-xl appearance-none border-2 border-gray-600 hover:border-purple-500 focus:border-purple-400 focus:ring-2 focus:ring-purple-400 focus:ring-opacity-50 text-sm sm:text-base font-medium transition-all cursor-pointer">
                {episodes.map(episode => {
                  const isReleased = isEpisodeReleased(episode.air_date);
                  const episodeTitle = episode.name?.length > 25 ? `${episode.name.substring(0, 25)}...` : episode.name || `Episode ${episode.episode_number}`;
                  return <option key={episode.id} value={episode.episode_number} disabled={!isReleased} className={`py-2 ${isReleased ? 'bg-gray-700 text-white' : 'bg-gray-800 text-gray-500'}`}>Episode {episode.episode_number}: {episodeTitle}{!isReleased && ` • Releases ${episode.air_date ? new Date(episode.air_date).toLocaleDateString() : 'TBD'}`}</option>
                })}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"><FiChevronDown className="text-gray-400 group-hover:text-purple-400 transition-colors duration-200 text-lg" /></div>
            </div>
            {/* Current Episode Info */}
            {currentEpisodeData && (
              <div className="bg-gray-750 rounded-xl p-4 border border-gray-600">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 bg-opacity-20 rounded-lg flex items-center justify-center"><span className="text-purple-400 font-bold text-sm">{selectedEpisode}</span></div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold text-sm sm:text-base mb-1">{currentEpisodeData.name || `Episode ${selectedEpisode}`}</h4>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400 mb-2">
                      <span>Season {selectedSeason}</span> • <span>Episode {selectedEpisode}</span>
                      {currentEpisodeData.air_date && <>• <span>{new Date(currentEpisodeData.air_date).toLocaleDateString()}</span></>}
                    </div>
                    {currentEpisodeData.overview && <p className="text-gray-300 text-xs sm:text-sm line-clamp-2">{currentEpisodeData.overview}</p>}
                    <div className="mt-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isEpisodeReleased(currentEpisodeData.air_date) ? 'bg-green-900 bg-opacity-50 text-green-300' : 'bg-red-900 bg-opacity-50 text-red-300'}`}>{isEpisodeReleased(currentEpisodeData.air_date) ? 'Available' : 'Not Released'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overview */}
      <div className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-700">
        <div className="flex items-center gap-2 mb-2 sm:mb-3"><FiInfo className="text-yellow-400 text-sm sm:text-base" /><h3 className="text-base sm:text-lg font-semibold text-white">Overview</h3></div>
        <p className="text-gray-300 text-sm sm:text-base">{item.overview}</p>
      </div>

      {/* Details Grid */}
      <div className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-700">
        <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 text-center">Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-1 text-center"><p className="text-gray-400 text-xs sm:text-sm">Genres</p><p className="text-gray-300 font-medium text-sm sm:text-base">{item.genre_ids?.map(id => getGenreName(id, item.media_type)).join(', ')}</p></div>
          <div className="space-y-1 text-center"><p className="text-gray-400 text-xs sm:text-sm">Status</p><p className="text-gray-300 font-medium text-sm sm:text-base">{item.status || 'N/A'}</p></div>
          {item.media_type === 'movie' ? (<div className="space-y-1 text-center"><p className="text-gray-400 text-xs sm:text-sm">Runtime</p><p className="text-gray-300 font-medium text-sm sm:text-base">{item.runtime ? `${Math.floor(item.runtime / 60)}h ${item.runtime % 60}m` : 'N/A'}</p></div>) : (<>
              <div className="space-y-1 text-center"><p className="text-gray-400 text-xs sm:text-sm">Total Seasons</p><p className="text-gray-300 font-medium text-sm sm:text-base">{item.number_of_seasons || 'N/A'}</p></div>
              <div className="space-y-1 text-center"><p className="text-gray-400 text-xs sm:text-sm">Total Episodes</p><p className="text-gray-300 font-medium text-sm sm:text-base">{item.number_of_episodes || 'N/A'}</p></div></>
          )}
        </div>
      </div>
    </>
  );
}